import SEO from "../components/SEO";
import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import toast from "react-hot-toast";
import { floatingIcons } from "../components/floatingIconsData";
import { useSearchParams } from "react-router-dom";

const ContactUs: React.FC = () => {

    const [searchParams] = useSearchParams();
    const companyEmail = "hello@landotravels.com";
    const phone = "+94722267400";

    const [email, setEmail] = useState("");
    const [subject, setSubject] = useState("");
    const [message, setMessage] = useState("");

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        try {
            await axios.post(
                `https://lando-travels.vercel.app/admin/contact`,
                {
                    email,
                    subject,
                    message,
                }
            );
            toast.success("Message sent successfully");
            setEmail("");
            setSubject("");
            setMessage("");
        } catch (error) {
            if (axios.isAxiosError(error)) {
                toast.error(error.response?.data?.message || "Failed to send message");
            } else {
                toast.error("Unexpected error occurred");
            }
        }
    };

    useEffect(() => {
        const defaultSubject = searchParams.get("subject") || "";
        const defaultMessage = searchParams.get("message") || "";

        setSubject(decodeURIComponent(defaultSubject));
        setMessage(decodeURIComponent(defaultMessage));
    }, []);

    return (
        <div className='justify-center items-center pb-20'>

            <SEO
  title="Contact Lando Travels | Sri Lanka Travel Experts"
  description="Contact Lando Travels for Sri Lanka tours, outbound travel packages, corporate travel services, flight bookings, visa assistance, and travel advice."
  canonical="https://www.landotravels.com/contact"
  image="/logo-white.webp"
/>
            {/* Cover Section */}
            <section className="relative w-full h-[70vh] md:h-[80vh] overflow-hidden">
                <img
                    src="/contactCover.webp"
                    alt="Contact Us"
                    loading="lazy"
                    className="absolute inset-0 w-full h-full object-cover z-0"
                />
                <div className="absolute inset-0 bg-black/40 z-0" />
                <motion.div
                    className="relative z-10 flex flex-col items-center text-center md:text-left md:items-end md:flex-row justify-between max-w-7xl mx-auto px-6 md:px-12 h-full pb-28"
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6 }}
                >
                    <h1 className="text-4xl md:text-6xl font-bold text-white mb-6 md:mb-0 mt-64 md:mt-0">
                        Contact Us
                    </h1>
                    <p className="text-white text-md md:text-lg max-w-xl text-center md:text-left">
                        Get in touch with our team for personalized travel solutions. We’re here to assist with visa support, ticketing, insurance, and expert guidance—every step of the way.
                    </p>
                </motion.div>
            </section>

            <section
                id="contact"
                className="relative min-h-screen bg-white text-black flex flex-col md:flex-row items-center justify-center px-6 md:px-24 overflow-hidden"
            >

                {/* Floating Background Icons */}
                <div className="absolute inset-0 z-0 overflow-hidden pointer-events-none">
                    {floatingIcons.map((item, index) => (
                        <div
                            key={index}
                            className="absolute text-blue-500/30 text-md animate-float"
                            style={{
                                top: item.top,
                                bottom: item.bottom,
                                left: item.left,
                                right: item.right,
                                animationDelay: item.delay,
                            }}
                        >
                            {item.icon}
                        </div>
                    ))}
                </div>

                <motion.div
                    className="w-full md:w-1/2 mb-12 md:mb-0"
                    initial={{ x: -100, opacity: 0 }}
                    whileInView={{ x: 0, opacity: 1 }}
                    transition={{ duration: 0.7, ease: "easeOut" }}
                    viewport={{ once: false, amount: 0.3 }}
                >
                    <div className="max-w-md">
                        <div className="mb-10">
                            <img
                                src="/logo-white.webp"
                                alt="landotravels Logo"
                                width={200}
                                height={100}
                                className="object-contain"
                            />
                        </div>
                        <h2 className="text-4xl mb-4 font-bold">Get in touch.</h2>
                        <p className="text-gray-500">
                            Need some help? Let us know what you need and we'll get straight
                            back to you.
                        </p>
                        <p className="mt-6">
                            <a aria-label="anchor tag"
                                href={`mailto:${companyEmail}`}
                                className="text-blue-500 text-sm hover:underline"
                            >
                                Email: {companyEmail}
                            </a>
                        </p>
                        <p>
                            <a aria-label="anchor tag"
                                href={`tel:${phone}`}
                                className="text-blue-500 text-sm hover:underline"
                            >
                                Phone: {phone}
                            </a>
                        </p>
                    </div>
                </motion.div>

                <motion.form
                    onSubmit={handleSubmit}
                    className="w-full md:w-1/2 grid grid-cols-1 md:grid-cols-2 gap-6"
                    initial={{ x: 100, opacity: 0 }}
                    whileInView={{ x: 0, opacity: 1 }}
                    transition={{ duration: 0.7, ease: "easeOut", delay: 0.2 }}
                    viewport={{ once: false, amount: 0.3 }}
                >
                    <div className="col-span-1">
                        <label htmlFor="label" className="text-sm text-gray-500 block mb-1">
                            Email Address
                        </label>
                        <input
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            className="rounded w-full bg-transparent border-b border-blue-500 focus:outline-none focus:border-b-2 focus:border-blue-500 transition"
                            placeholder="example@gmail.com"
                            required
                        />
                    </div>
                    <div className="col-span-1">
                        <label htmlFor="label" className="text-sm text-gray-500 block mb-1">Subject</label>
                        <input
                            type="text"
                            value={subject}
                            onChange={(e) => setSubject(e.target.value)}
                            className="rounded w-full bg-transparent border-b border-blue-500 focus:outline-none focus:border-b-2 focus:border-blue-500 transition"
                            placeholder="Your Subject"
                            required
                        />
                    </div>
                    <div className="col-span-2">
                        <label htmlFor="label" className="text-sm text-gray-500 block mb-1">Message</label>
                        <textarea
                            rows={4}
                            value={message}
                            onChange={(e) => setMessage(e.target.value)}
                            className="rounded w-full bg-transparent border-b border-blue-500 focus:outline-none focus:border-b-2 focus:border-blue-500 transition resize-none"
                            placeholder="Start typing here..."
                            required
                        />
                    </div>
                    <div className="col-span-2 text-right">
                        <button aria-label="Button"
                            type="submit"
                            className="bg-blue-500 text-white text-sm cursor-pointer font-medium px-5 py-2 rounded-lg hover:bg-blue-600 transition"
                        >
                            Send
                        </button>
                    </div>
                </motion.form>
            </section>
        </div>
    );
};

export default ContactUs;
